import i18next from 'i18next'
import { useAtom } from 'jotai'
import { useTranslation } from 'react-i18next'
import { WidgetFontSelectDropdown } from '../../constructs'
import { Glyphs, WidgetInputToggleField, WidgetMicroMenu, WidgetSelectGroup } from '../../rapid-ui'
import {
	feedAdMetaAttributionPositionAtom,
	feedAdMetaCardsAtom,
	feedAdMetaGeneralContentFontFamilyOverrideAtom,
	feedAdMetaGeneralContentHideAtom,
	feedAdMetaLocationPositionAtom,
	feedAdMetaLogoBackgroundFillColorAtom,
	feedAdMetaLogoPositionAtom,
	feedAdMetaStackOrientationAtom,
} from '../state'
import { trackLayerInteracted } from '../tracking'
import { EditorWidgetColorField } from './shared/editor-widget-color-field'

const positionOptions = {
	topLeft: {
		id: 'top-left',
		render: () => <Glyphs.core.LayoutTopLeftIcon />,
		label: i18next.t('editor:contentLayers.topLeft'),
	},
	topRight: {
		id: 'top-right',
		render: () => <Glyphs.core.LayoutTopRightIcon />,
		label: i18next.t('editor:contentLayers.topRight'),
	},
	bottomLeft: {
		id: 'bottom-left',
		render: () => <Glyphs.core.LayoutBottomLeftIcon />,
		label: i18next.t('editor:contentLayers.bottomLeft'),
	},
	bottomRight: {
		id: 'bottom-right',
		render: () => <Glyphs.core.LayoutBottomRightIcon />,
		label: i18next.t('editor:contentLayers.bottomRight'),
	},
	hide: {
		id: 'hide',
		render: () => <Glyphs.core.HideIcon />,
		label: i18next.t('editor:contentLayers.hide'),
	},
} as const

const logoOptionPositions = [
	positionOptions.topLeft,
	positionOptions.topRight,
	positionOptions.bottomLeft,
	positionOptions.bottomRight,
	positionOptions.hide,
]

const locationOptionPositions = [
	positionOptions.topLeft,
	positionOptions.topRight,
	positionOptions.bottomLeft,
	positionOptions.bottomRight,
	positionOptions.hide,
]

const attributionOptionPositions = [
	positionOptions.topRight,
	positionOptions.bottomRight,
	positionOptions.hide,
]

const stackOrientationOptionPositions = [
	{
		id: 'vertical',
		render: () => <>{i18next.t('editor:contentLayers.vertical')}</>,
		label: i18next.t('editor:contentLayers.vertical'),
	},
	{
		id: 'horizontal',
		render: () => <>{i18next.t('editor:contentLayers.horizontal')}</>,
		label: i18next.t('editor:contentLayers.horizontal'),
	},
]

export function FeedAdContentLayerControls({}: {}) {
	const { t } = useTranslation('editor')
	const [logoPosition, setLogoPosition] = useAtom(feedAdMetaLogoPositionAtom)
	const [logoBackgroundFillColor, setLogoBackgroundFillColor] = useAtom(feedAdMetaLogoBackgroundFillColorAtom)
	const [locationPosition, setLocationPosition] = useAtom(feedAdMetaLocationPositionAtom)
	const [attributionPosition, setAttributionPosition] = useAtom(feedAdMetaAttributionPositionAtom)
	const [fontFamilyOverride, setFontFamilyOverride] = useAtom(feedAdMetaGeneralContentFontFamilyOverrideAtom)
	const [hide, setHide] = useAtom(feedAdMetaGeneralContentHideAtom)
	const [cardConfiguration, setCardConfiguration] = useAtom(feedAdMetaCardsAtom)

	const [stackOrientation, setStackOrientation] = useAtom(feedAdMetaStackOrientationAtom)

	const isStateReady = cardConfiguration
		&& logoPosition !== undefined
		&& logoBackgroundFillColor !== undefined
		&& locationPosition !== undefined
		&& attributionPosition !== undefined
		&& stackOrientation !== undefined

	if (!isStateReady) {
		return null
	}

	return (
		<>
			<div className="text-left ml-10 mb-4 grid grid-cols-[auto_1fr] items-center gap-2">
				<div className="text-xs text-grey-600">
					{t('contentLayers.showContent')}
				</div>

				<div className="">
					<WidgetInputToggleField
						value={!(hide ?? false)}
						onChange={(value) => {
							trackLayerInteracted('content_visibility_toggled', 'feed')
							setHide(!value)
						}}
					/>
				</div>

				<div className="text-xs text-grey-600">
					{t('contentLayers.font')}
				</div>
				<div className="grid items-center gap-2 grid-cols-[1fr_auto]">
					<WidgetFontSelectDropdown
						value={fontFamilyOverride ?? ''}
						onChange={(value) => {
							trackLayerInteracted('font_changed', 'feed')
							setFontFamilyOverride(value)
						}}
						allowDefault={true}
					/>
					<WidgetMicroMenu
						items={[
							{
								id: 'reset',
								label: t('contentLayers.resetToDefault'),
								onSelect: () => {
									trackLayerInteracted('font_reset', 'feed')
									setFontFamilyOverride('')
								},
							},
						]}
					/>
				</div>

				{
					/* <div />
				<div className="text-xs text-grey-500 flex items-center gap-1">
					<Glyphs.micro.InfoIcon />
					Some elements have custom fonts applied.
				</div> */
				}

				<div className="text-xs text-grey-600">
					{t('contentLayers.showCard')}
				</div>
				<div className="grid items-center gap-2 grid-cols-[auto_1fr]">
					<WidgetInputToggleField
						value={cardConfiguration.show}
						onChange={(value) => {
							trackLayerInteracted('card_visibility_toggled', 'feed')
							setCardConfiguration({ ...cardConfiguration, show: value })
						}}
					/>
					{cardConfiguration.show && (
						<EditorWidgetColorField
							value={cardConfiguration.color}
							onChange={(value) => {
								trackLayerInteracted('card_color_changed', 'feed')
								setCardConfiguration({ ...cardConfiguration, color: value })
							}}
							label={t('contentLayers.cardColor')}
						/>
					)}
				</div>

				<div className="text-xs text-grey-600">
					{t('contentLayers.logo')}
				</div>
				<WidgetSelectGroup
					value={logoPosition}
					onChange={(value) => {
						trackLayerInteracted('logo_position_changed', 'feed')
						setLogoPosition(value)
					}}
					items={logoOptionPositions}
				/>
				{logoPosition !== 'hide' && (
					<>
						<div className="text-xs text-grey-600">
							{t('contentLayers.logoBackground')}
						</div>
						<EditorWidgetColorField
							value={logoBackgroundFillColor}
							onChange={(value) => {
								trackLayerInteracted('logo_background_color_changed', 'feed')
								setLogoBackgroundFillColor(value)
							}}
							label={t('contentLayers.logoBackground')}
							includeTransparent
						/>
					</>
				)}
				<div className="text-xs text-grey-600">
					{t('contentLayers.location')}
				</div>
				<WidgetSelectGroup
					value={locationPosition}
					onChange={(value) => {
						trackLayerInteracted('location_position_changed', 'feed')
						setLocationPosition(value)
					}}
					items={locationOptionPositions}
				/>
				<div className="text-xs text-grey-600">
					{t('contentLayers.attribution')}
				</div>
				<WidgetSelectGroup
					value={attributionPosition}
					onChange={(value) => {
						trackLayerInteracted('attribution_position_changed', 'feed')
						setAttributionPosition(value)
					}}
					items={attributionOptionPositions}
				/>

				<div className="text-xs text-grey-600">
					{t('contentLayers.stackOrientation')}
				</div>
				<WidgetSelectGroup
					value={stackOrientation}
					onChange={(value) => {
						trackLayerInteracted('stack_orientation_changed', 'feed')
						setStackOrientation(value as 'vertical' | 'horizontal')
					}}
					items={stackOrientationOptionPositions}
				/>
			</div>
		</>
	)
}
